import React, {ReactNode} from 'react';

import {Box, CircularProgress, Typography} from '@material-ui/core';

import {lshFormatCurrency, lshFormatInteger} from './analyticsData';
import {AnalyticsBreakdownRow, AnalyticsTopProductRow} from './analyticsTypes';

export function AnalyticsSection(props: {
  title: string;
  children: ReactNode;
  loading?: boolean;
}) {
  return (
    <section className="analytics-section">
      <div className="analytics-section-header">
        <Typography className="analytics-section-title">
          {props.title}
        </Typography>
        {props.loading && <CircularProgress size={18} thickness={5} />}
      </div>
      {props.children}
    </section>
  );
}

export function AnalyticsMetric(props: {
  label: string;
  value: string;
  detail: string;
}) {
  return (
    <div className="analytics-metric">
      <Typography className="analytics-metric-label">{props.label}</Typography>
      <Typography className="analytics-metric-value">{props.value}</Typography>
      <Typography className="analytics-metric-detail">
        {props.detail}
      </Typography>
    </div>
  );
}

export function AnalyticsCard(props: {
  title: string;
  subTitle?: string;
  children?: ReactNode;
}) {
  return (
    <Box className="analytics-card">
      <Typography variant="subtitle1">{props.title}</Typography>
      {props.subTitle && (
        <Typography variant="body2" color="textSecondary" gutterBottom>
          {props.subTitle}
        </Typography>
      )}
      <Box className="chart-container" mt={1}>
        {props.children}
      </Box>
    </Box>
  );
}

export function AnalyticsBreakdownList(props: {
  title: string;
  rows: AnalyticsBreakdownRow[];
  valueLabel: string;
  valueFormatter: (value: number) => string;
}) {
  return (
    <div className="analytics-list-card">
      <Typography className="analytics-list-title">{props.title}</Typography>
      {props.rows.length ? (
        <div className="analytics-list">
          {props.rows.map((row) => (
            <div className="analytics-list-row" key={row.label}>
              <div className="analytics-list-row-main">
                <span>{row.label}</span>
                <strong>{row.count ?? 0}</strong>
              </div>
              <div className="analytics-list-row-meta">
                <span>
                  {props.valueLabel}: {props.valueFormatter(row.value || 0)}
                </span>
                <span>{row.percent || 0}%</span>
              </div>
              <div className="analytics-list-row-track">
                <span style={{width: `${Math.min(row.percent || 0, 100)}%`}} />
              </div>
            </div>
          ))}
        </div>
      ) : (
        <AnalyticsEmptyState />
      )}
    </div>
  );
}

export function AnalyticsTopProductsList(props: {
  title: string;
  rows: AnalyticsTopProductRow[];
}) {
  return (
    <div className="analytics-list-card">
      <Typography className="analytics-list-title">{props.title}</Typography>
      {props.rows.length ? (
        <div className="analytics-table-list">
          <div className="analytics-table-header">
            <span>Product</span>
            <span>Qty</span>
            <span>Value</span>
          </div>
          {props.rows.map((row) => (
            <div className="analytics-table-row" key={row.name}>
              <span>{row.name}</span>
              <span>{lshFormatInteger(row.quantity)}</span>
              <span>{lshFormatCurrency(row.value)}</span>
            </div>
          ))}
        </div>
      ) : (
        <AnalyticsEmptyState />
      )}
    </div>
  );
}

function AnalyticsEmptyState() {
  return (
    <Typography variant="body2" color="textSecondary">
      No data available
    </Typography>
  );
}
